import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { HalError } from "~/lib/hal";
import { redirectToLoginOnExpiredSession } from "~/lib/sessionExpired";

// The single QueryClient instance for the app. Configured with
// conservative defaults that suit a HAL API:
//
//   - staleTime: 30s. Most resources don't change between page
//     navigations within a short window. 30s gives the cache a useful
//     hit rate without serving stale data for long.
//   - retry: don't retry on 4xx. A 401/403/404 is not a network
//     glitch — it's a real answer from the backend and retrying
//     masks the problem. 5xx and network errors still retry up to
//     2 times with exponential backoff (TanStack Query defaults).
//   - refetchOnWindowFocus: off. For an internal tool with cookie
//     sessions, aggressive refetch on focus is noise. We'll turn it
//     back on if we see stale-data bugs in practice.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: redirectToLoginOnExpiredSession }),
  mutationCache: new MutationCache({ onError: redirectToLoginOnExpiredSession }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx — the backend is telling us something
        // real. Retrying 401 in a loop would be particularly bad
        // (rapid-fire auth failures).
        if (error instanceof HalError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // Mutations don't auto-retry — the user is waiting, a silent
      // retry of a failed POST would be surprising. Callers can opt
      // in explicitly if they have an idempotent mutation.
      retry: false,
    },
  },
});
